// Paddle SDK initialization and utilities

let paddleInitialized = false;
let paddleInitPromise: Promise<void> | null = null;

export async function initializePaddle(clientToken: string, environment: 'production' | 'sandbox'): Promise<void> {
  if (paddleInitialized) {
    console.log('[Paddle] Already initialized');
    return;
  }

  if (paddleInitPromise) {
    console.log('[Paddle] Initialization already in progress, waiting...');
    return paddleInitPromise;
  }

  paddleInitPromise = new Promise((resolve, reject) => {
    console.log('[Paddle] Starting initialization...', { environment, hasToken: !!clientToken });

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds total (50 * 100ms)

    // Wait for Paddle to be available
    const checkPaddle = () => {
      attempts++;
      
      if (!(window as any).Paddle) {
        if (attempts >= maxAttempts) {
          const error = new Error('Paddle.js failed to load after 5 seconds');
          console.error('[Paddle] Timeout:', error);
          reject(error);
          return;
        }
        console.log('[Paddle] Waiting for Paddle.js to load... (attempt', attempts, '/', maxAttempts, ')');
        setTimeout(checkPaddle, 100);
        return;
      }

      const Paddle = (window as any).Paddle;
      console.log('[Paddle] Paddle.js loaded, initializing with token...');

      try {
        // Set environment first
        if (environment === 'sandbox') {
          try {
            Paddle.Environment?.set('sandbox');
            console.log('[Paddle] Environment set to sandbox');
          } catch (e) {
            console.warn('[Paddle] Could not set environment:', e);
          }
        }

        // Initialize with token - try both methods for compatibility
        if (typeof Paddle.Initialize === 'function') {
          Paddle.Initialize({ 
            token: clientToken,
            eventCallback: (event: any) => {
              console.log('[Paddle] Event:', event?.name || 'NO_NAME', event?.data || 'NO_DATA', event);
              
              // Log errors and important events with more detail
              if (event?.name === 'checkout.error') {
                console.error('[Paddle] Checkout error event:', {
                  name: event.name,
                  data: event.data,
                  error: event.error,
                  detail: event.detail,
                  fullEvent: event,
                });
              }
              
              if (event?.name === 'checkout.closed') {
                console.log('[Paddle] Checkout closed event:', {
                  name: event.name,
                  data: event.data,
                  reason: event.reason,
                  fullEvent: event,
                });
              }

              if (event?.name === 'checkout.completed') {
                console.log('[Paddle] Checkout completed event:', {
                  name: event.name,
                  data: event.data,
                  fullEvent: event,
                });
              }
            }
          });
          console.log('[Paddle] Initialized with Paddle.Initialize');
        } else if (typeof Paddle.Setup === 'function') {
          Paddle.Setup({ 
            token: clientToken,
            eventCallback: (event: any) => {
              console.log('[Paddle] Event:', event?.name || 'NO_NAME', event?.data || 'NO_DATA', event);
              
              // Log errors and important events with more detail
              if (event?.name === 'checkout.error') {
                console.error('[Paddle] Checkout error event:', {
                  name: event.name,
                  data: event.data,
                  error: event.error,
                  detail: event.detail,
                  fullEvent: event,
                });
              }
              
              if (event?.name === 'checkout.closed') {
                console.log('[Paddle] Checkout closed event:', {
                  name: event.name,
                  data: event.data,
                  reason: event.reason,
                  fullEvent: event,
                });
              }

              if (event?.name === 'checkout.completed') {
                console.log('[Paddle] Checkout completed event:', {
                  name: event.name,
                  data: event.data,
                  fullEvent: event,
                });
              }
            }
          });
          console.log('[Paddle] Initialized with Paddle.Setup');
        } else {
          console.warn('[Paddle] No initialization method found, proceeding anyway');
        }

        paddleInitialized = true;
        console.log('[Paddle] ✅ Initialization complete');
        resolve();
      } catch (err) {
        console.error('[Paddle] Initialization error:', err);
        reject(err);
      }
    };

    checkPaddle();
  });

  return paddleInitPromise;
}

export function isPaddleInitialized(): boolean {
  return paddleInitialized;
}

export async function waitForPaddleReady(): Promise<void> {
  if (paddleInitialized) {
    return Promise.resolve();
  }
  
  if (paddleInitPromise) {
    return paddleInitPromise;
  }

  // If not initialized yet, wait for global Paddle to be available
  return new Promise((resolve) => {
    const check = () => {
      if ((window as any).Paddle) {
        resolve();
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
}
